"use client";

import React, { useState } from "react";
import { Clock, CalendarDays, ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";

const DAY_ORDER = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

// JS Date.getDay() → day key (0 = Sunday)
const JS_DAY = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

function parseMinutes(time) {
  if (!time || typeof time !== "string") return null;
  const m = time.trim().toLowerCase().match(/^(\d{1,2}):(\d{2})\s*(am|pm)?$/);
  if (!m) return null;
  let h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  if (m[3] === "pm" && h !== 12) h += 12;
  if (m[3] === "am" && h === 12) h = 0;
  return h * 60 + min;
}

function formatTime24h(time) {
  const mins = parseMinutes(time);
  if (mins == null) return "";
  return `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(
    mins % 60,
  ).padStart(2, "0")}`;
}

export default function WorkerOpeningHours({ workDays, defaultExpanded = false }) {
  const { t } = useTranslation();
  const [showWeek, setShowWeek] = useState(defaultExpanded);

  if (!Array.isArray(workDays) || workDays.length === 0) return null;

  const byDay = Object.fromEntries(workDays.map((d) => [d.day, d]));
  const todayKey = JS_DAY[new Date().getDay()];

  const getRanges = (key) => {
    const d = byDay[key];
    return d?.active ? d.dayTimeRanges ?? [] : [];
  };
  const dayLabel = (key) => t(`workingDays.days.${key}`);
  const fmtRange = (r) =>
    `${formatTime24h(r.start_time)} – ${formatTime24h(r.end_time)}`;

  const todayRanges = getRanges(todayKey);

  return (
    // Keep interactions here from bubbling to a parent click handler (worker list card).
    <div
      onClick={(e) => e.stopPropagation()}
      className="flex flex-col gap-3 rounded-2xl border border-[#EDEEF3] bg-white p-4"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="rounded-lg bg-[#EEF0FF] p-1.5">
            <Clock className="h-4 w-4 text-[#5563F6]" />
          </div>
          <span className="text-xs font-bold uppercase tracking-wide text-[#302B2B]">
            {t("home.openingHours")}
          </span>
        </div>
      </div>

      {/* Today */}
      <div className="rounded-xl bg-[#F9FAFB] p-3">
        <div className="mb-2 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm">
            <span className="h-2 w-2 rounded-full bg-[#5563F6]" />
            <span className="text-[#9A99AB]">{t("home.today")},</span>
            <span className="font-bold text-[#302B2B]">
              {dayLabel(todayKey)}
            </span>
          </div>
          {todayRanges.length > 1 && (
            <span className="text-[11px] font-medium uppercase text-[#9A99AB]">
              {t("home.shiftsCount", { count: todayRanges.length })}
            </span>
          )}
        </div>
        {todayRanges.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {todayRanges.map((r, i) => (
              <span
                key={i}
                className="rounded-lg border border-[#EDEEF3] bg-white px-3 py-1.5 text-sm font-semibold text-[#302B2B]"
              >
                {fmtRange(r)}
              </span>
            ))}
          </div>
        ) : (
          <span className="text-sm text-[#9A99AB]">
            {t("home.statusClosed")}
          </span>
        )}
      </div>

      {/* Toggle full week */}
      <button
        type="button"
        onClick={() => setShowWeek((v) => !v)}
        className="flex items-center justify-between rounded-xl border border-[#EDEEF3] px-3 py-2.5 text-sm font-medium text-[#302B2B] transition hover:bg-[#F9FAFB]"
      >
        <span className="flex items-center gap-2">
          <CalendarDays className="h-4 w-4 text-[#5563F6]" />
          {showWeek ? t("home.hideFullWeek") : t("home.showFullWeek")}
        </span>
        <ChevronDown
          className={`h-4 w-4 transition-transform ${
            showWeek ? "rotate-180" : ""
          }`}
        />
      </button>

      {/* Full week */}
      {showWeek && (
        <div className="flex flex-col">
          {DAY_ORDER.map((key) => {
            const ranges = getRanges(key);
            const open = ranges.length > 0;
            const isToday = key === todayKey;
            return (
              <div
                key={key}
                className={`flex items-start justify-between gap-3 rounded-lg border-b border-[#F1F2F6] px-2 py-3 last:border-b-0 ${
                  isToday ? "bg-[#F3F2FF]" : ""
                }`}
              >
                <div className="flex items-center gap-2">
                  <span
                    className={`h-2 w-2 rounded-full ${
                      open ? "bg-[#4DB774]" : "bg-[#D4D3DD]"
                    }`}
                  />
                  <span
                    className={`text-sm ${
                      isToday ? "font-bold text-[#5563F6]" : "text-[#302B2B]"
                    }`}
                  >
                    {dayLabel(key)}
                  </span>
                  {isToday && (
                    <span className="rounded-md bg-[#E7E6F7] px-1.5 py-0.5 text-[10px] font-bold uppercase text-[#5563F6]">
                      {t("home.today")}
                    </span>
                  )}
                </div>
                <div
                  className={`text-right text-sm ${
                    isToday ? "font-bold text-[#302B2B]" : "text-[#6B6A7B]"
                  }`}
                >
                  {open ? (
                    ranges.map((r, i) => <div key={i}>{fmtRange(r)}</div>)
                  ) : (
                    <span className="text-[#9A99AB]">
                      {t("home.statusClosed")}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
